import { computed, makeObservable, runInAction } from "mobx";
import { liveQuery, Subscription } from "dexie";
import { BaseError } from "@/domains/errors";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { UpdateSpaceAccountSettingsOperation } from "@/store/sync/operations/space-account-settings/UpdateSpaceAccountSettingsOperation";
import {
  CalendarSettings,
  ChatSettings,
  MemAgentSettings,
  SpaceAccountSettingsModelData,
  SpaceAccountSettingsPatch,
  VoiceSettings,
} from "./types";
import { SpaceAccountSettingsModel } from "./SpaceAccountSettingsModel";
import {
  normalizeMemAgentHoldToTalkKey,
  type MemAgentHoldToTalkKeySetting,
} from "@/modules/mem-agent/hold-to-talk-key";

type PendingApiSettingsPatch = {
  modelId: string;
  modelVersion: number;
  settingsPatch: SpaceAccountSettingsPatch;
};

type SyncedSettingsSnapshot = {
  modelId: string;
  modelVersion: number;
  settings: SpaceAccountSettingsModelData["settings"];
};

/**
 * Observable wrapper for voice settings that provides convenient getters.
 */
class VoiceSettingsObservable {
  constructor(private readonly getVoiceSettings: () => VoiceSettings | undefined) {
    makeObservable(this, {
      customVocabulary: computed,
      processingInstructions: computed,
    });
  }

  get customVocabulary(): string[] {
    return this.getVoiceSettings()?.custom_vocabulary ?? [];
  }

  get processingInstructions(): string {
    return this.getVoiceSettings()?.processing_instructions ?? "";
  }
}

/**
 * Observable wrapper for calendar settings that provides convenient getters.
 */
class CalendarSettingsObservable {
  constructor(private readonly getCalendarSettings: () => CalendarSettings | undefined) {
    makeObservable(this, {
      hideCalendarIntegrationShill: computed,
      showCalendarEventsWithNoAttendees: computed,
    });
  }

  get hideCalendarIntegrationShill(): boolean {
    return this.getCalendarSettings()?.hide_calendar_integration_shill ?? false;
  }

  get showCalendarEventsWithNoAttendees(): boolean {
    return this.getCalendarSettings()?.show_calendar_events_with_no_attendees ?? true;
  }
}

/**
 * Observable wrapper for chat settings that provides convenient getters.
 */
class ChatSettingsObservable {
  constructor(private readonly getChatSettings: () => ChatSettings | undefined) {
    makeObservable(this, {
      instructions: computed,
    });
  }

  get instructions(): string {
    return this.getChatSettings()?.instructions ?? "";
  }
}

/**
 * Observable wrapper for Mem Agent settings that provides convenient getters.
 */
class MemAgentSettingsObservable {
  constructor(private readonly getMemAgentSettings: () => MemAgentSettings | undefined) {
    makeObservable(this, {
      holdToTalkKey: computed,
      holdToTalkIsEnabled: computed,
      remindersCalloutDismissedAt: computed,
      remindersCalloutIsDismissible: computed,
      proactiveTrialBannerIsDismissed: computed,
    });
  }

  get holdToTalkKey(): MemAgentHoldToTalkKeySetting {
    return normalizeMemAgentHoldToTalkKey(this.getMemAgentSettings()?.hold_to_talk_key);
  }

  get holdToTalkIsEnabled(): boolean {
    return this.getMemAgentSettings()?.hold_to_talk_is_enabled ?? true;
  }

  get remindersCalloutDismissedAt(): string | null {
    return this.getMemAgentSettings()?.reminders_callout_dismissed_at ?? null;
  }

  get remindersCalloutIsDismissible(): boolean {
    return this.getMemAgentSettings()?.reminders_callout_is_dismissible ?? false;
  }

  get proactiveTrialBannerIsDismissed(): boolean {
    return this.getMemAgentSettings()?.proactive_trial_banner_is_dismissed ?? false;
  }
}

export class AppStoreSpaceAccountSettingsStore extends BaseSyncModelStore<
  SpaceAccountSettingsModel,
  SpaceAccountSettingsModelData
> {
  private _voiceSettings: VoiceSettings | undefined;
  private _calendarSettings: CalendarSettings | undefined;
  private _chatSettings: ChatSettings | undefined;
  private _memAgentSettings: MemAgentSettings | undefined;
  private syncedSettingsSnapshot: SyncedSettingsSnapshot | undefined;
  private pendingApiSettingsPatches: PendingApiSettingsPatch[] = [];
  private liveQuerySubscription: Subscription | undefined;
  private hasInitialized = false;

  public readyState: AsyncResult<boolean> = asyncResultModule.setLoading();

  /** Nested observable for voice settings with convenient getters */
  public readonly voice: VoiceSettingsObservable;

  /** Nested observable for calendar settings with convenient getters */
  public readonly calendar: CalendarSettingsObservable;

  /** Nested observable for chat settings with convenient getters */
  public readonly chat: ChatSettingsObservable;

  /** Nested observable for Mem Agent settings with convenient getters */
  public readonly memAgent: MemAgentSettingsObservable;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountSettings, ...injectedDeps });

    this.voice = new VoiceSettingsObservable(() => this._voiceSettings);
    this.calendar = new CalendarSettingsObservable(() => this._calendarSettings);
    this.chat = new ChatSettingsObservable(() => this._chatSettings);
    this.memAgent = new MemAgentSettingsObservable(() => this._memAgentSettings);

    makeObservable<
      AppStoreSpaceAccountSettingsStore,
      | "_voiceSettings"
      | "_calendarSettings"
      | "_chatSettings"
      | "_memAgentSettings"
      | "syncedSettingsSnapshot"
      | "pendingApiSettingsPatches"
      | "hasInitialized"
      | "liveQuerySubscription"
      | "waitForDbAndStartLiveQuery"
      | "startLiveQuery"
      | "handleLiveQueryResult"
      | "handleLiveQueryError"
      | "unsubscribeLiveQuery"
      | "applySettingsPatchToState"
      | "reconcileSettingsState"
    >(this, {
      _voiceSettings: true,
      _calendarSettings: true,
      _chatSettings: true,
      _memAgentSettings: true,
      syncedSettingsSnapshot: false,
      pendingApiSettingsPatches: false,
      hasInitialized: true,
      readyState: true,
      voice: false,
      calendar: false,
      chat: false,
      memAgent: false,
      liveQuerySubscription: false,
      waitForDbAndStartLiveQuery: false,
      startLiveQuery: false,
      handleLiveQueryResult: false,
      handleLiveQueryError: false,
      unsubscribeLiveQuery: false,
      applySettingsPatchToState: false,
      reconcileSettingsState: false,
      createSyncModel: false,
      updateSettingsUsingApi: false,
      updateSettingsUsingSyncOperation: false,
      voiceSettings: computed,
      calendarSettings: computed,
      chatSettings: computed,
      memAgentSettings: computed,
      // Keep top-level getters for backward compatibility
      customVocabulary: computed,
      processingInstructions: computed,
    });

    this.waitForDbAndStartLiveQuery();
  }

  /**
   * Waits for MemDB to be ready, then starts the liveQuery.
   * We use setInterval because this store is constructed before AppStore's
   * makeAutoObservable() runs, so MobX reactivity isn't set up yet.
   */
  private waitForDbAndStartLiveQuery(): void {
    const waitForDb = setInterval(() => {
      if (this.store.isMemDbReady) {
        clearInterval(waitForDb);
        this.startLiveQuery();
      }
    }, 100);
  }

  private startLiveQuery(): void {
    if (this.liveQuerySubscription) return;

    const startTime = performance.now();
    logger.info({
      message: "[AppStoreSpaceAccountSettingsStore] Starting liveQuery for settings",
    });

    try {
      this.liveQuerySubscription = liveQuery(() => this.localTable.limit(1).toArray()).subscribe({
        next: results => this.handleLiveQueryResult(results, startTime),
        error: err => this.handleLiveQueryError(err),
      });
    } catch (error) {
      this.handleLiveQueryError(error as Error);
    }
  }

  private handleLiveQueryResult(
    results: Array<SyncUpdateValue<SpaceAccountSettingsModelData>>,
    startTime: number
  ): void {
    const settingsRecord = results[0];
    const settingsData = settingsRecord?.model_data.settings;
    const pendingApiSettingsPatches = this.pendingApiSettingsPatches.filter(
      pendingPatch =>
        settingsRecord?.model_id !== pendingPatch.modelId ||
        settingsRecord.model_version < pendingPatch.modelVersion
    );
    pendingApiSettingsPatches.sort((a, b) => a.modelVersion - b.modelVersion);
    this.pendingApiSettingsPatches = pendingApiSettingsPatches;

    runInAction(() => {
      if (settingsData) {
        this.syncedSettingsSnapshot = {
          modelId: settingsRecord.model_id,
          modelVersion: settingsRecord.model_version,
          settings: settingsData,
        };
      }
      this.reconcileSettingsState();

      // Handle first-time initialization
      if (!this.hasInitialized) {
        this.hasInitialized = true;
        this.readyState = asyncResultModule.setReady(true);

        const elapsedMs = Math.round(performance.now() - startTime);
        logger.info({
          message: settingsRecord
            ? "[AppStoreSpaceAccountSettingsStore] Settings loaded from local storage"
            : "[AppStoreSpaceAccountSettingsStore] No settings found in local storage (new user or not synced yet)",
          info: {
            elapsedMs,
            hasVoiceSettings: !!settingsData?.voice,
            hasCalendarSettings: !!settingsData?.calendar,
            hasChatSettings: !!settingsData?.chat,
            hasMemAgentSettings: !!settingsData?.mem_agent,
          },
        });
      }
    });
  }

  private handleLiveQueryError(error: Error): void {
    this.unsubscribeLiveQuery();

    const err =
      error instanceof BaseError
        ? error
        : new BaseError({
            message: "[AppStoreSpaceAccountSettingsStore] Failed to initialize settings",
            info: { originalError: objectModule.safeErrorAsJson(error) },
          });

    logger.error({
      message: "[AppStoreSpaceAccountSettingsStore] Error in liveQuery",
      info: { err: objectModule.safeErrorAsJson(err) },
    });

    runInAction(() => {
      // Don't block the app from rendering because of settings errors
      this.hasInitialized = true;
      this.readyState = asyncResultModule.setReady(true);
    });
  }

  private unsubscribeLiveQuery(): void {
    if (this.liveQuerySubscription) {
      this.liveQuerySubscription.unsubscribe();
      this.liveQuerySubscription = undefined;
    }
  }

  private applySettingsPatchToState(settingsPatch: SpaceAccountSettingsPatch): void {
    const voicePatch = settingsPatch.voice;
    if (voicePatch) {
      this._voiceSettings = {
        ...this._voiceSettings,
        ...(voicePatch.custom_vocabulary != null
          ? { custom_vocabulary: voicePatch.custom_vocabulary }
          : {}),
        ...(voicePatch.processing_instructions != null
          ? { processing_instructions: voicePatch.processing_instructions }
          : {}),
      };
    }

    const calendarPatch = settingsPatch.calendar;
    if (calendarPatch) {
      this._calendarSettings = {
        ...this._calendarSettings,
        ...(calendarPatch.hide_calendar_integration_shill != null
          ? { hide_calendar_integration_shill: calendarPatch.hide_calendar_integration_shill }
          : {}),
        ...(calendarPatch.enable_calendar_event_notifications != null
          ? {
              enable_calendar_event_notifications:
                calendarPatch.enable_calendar_event_notifications,
            }
          : {}),
        ...(calendarPatch.enable_unscheduled_meeting_notifications != null
          ? {
              enable_unscheduled_meeting_notifications:
                calendarPatch.enable_unscheduled_meeting_notifications,
            }
          : {}),
        ...(calendarPatch.show_calendar_events_with_no_attendees != null
          ? {
              show_calendar_events_with_no_attendees:
                calendarPatch.show_calendar_events_with_no_attendees,
            }
          : {}),
      };
    }

    const chatPatch = settingsPatch.chat;
    if (chatPatch?.instructions != null) {
      this._chatSettings = {
        ...this._chatSettings,
        instructions: chatPatch.instructions,
      };
    }

    const memAgentPatch = settingsPatch.mem_agent;
    if (memAgentPatch) {
      const nextMemAgentSettings = { ...this._memAgentSettings };

      if (memAgentPatch.reminders_callout_dismissed_at !== undefined) {
        nextMemAgentSettings.reminders_callout_dismissed_at =
          memAgentPatch.reminders_callout_dismissed_at;
      }

      if (memAgentPatch.reminders_callout_is_dismissible !== undefined) {
        nextMemAgentSettings.reminders_callout_is_dismissible =
          memAgentPatch.reminders_callout_is_dismissible;
      }

      if (memAgentPatch.proactive_trial_banner_is_dismissed !== undefined) {
        nextMemAgentSettings.proactive_trial_banner_is_dismissed =
          memAgentPatch.proactive_trial_banner_is_dismissed;
      }

      if (Object.prototype.hasOwnProperty.call(memAgentPatch, "hold_to_talk_key")) {
        nextMemAgentSettings.hold_to_talk_key = memAgentPatch.hold_to_talk_key;
      }

      if (memAgentPatch.hold_to_talk_is_enabled !== undefined) {
        nextMemAgentSettings.hold_to_talk_is_enabled = memAgentPatch.hold_to_talk_is_enabled;
      }

      this._memAgentSettings = nextMemAgentSettings;
    }
  }

  private reconcileSettingsState(): void {
    const syncedSettings = this.syncedSettingsSnapshot?.settings;
    this._voiceSettings = syncedSettings?.voice;
    this._calendarSettings = syncedSettings?.calendar;
    this._chatSettings = syncedSettings?.chat;
    this._memAgentSettings = syncedSettings?.mem_agent;

    for (const pendingPatch of this.pendingApiSettingsPatches) {
      this.applySettingsPatchToState(pendingPatch.settingsPatch);
    }
  }

  createSyncModel(data: SyncUpdateValue<SpaceAccountSettingsModelData>): SpaceAccountSettingsModel {
    return new SpaceAccountSettingsModel({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  get voiceSettings(): VoiceSettings | undefined {
    return this._voiceSettings;
  }

  get calendarSettings(): CalendarSettings | undefined {
    return this._calendarSettings;
  }

  get chatSettings(): ChatSettings | undefined {
    return this._chatSettings;
  }

  get memAgentSettings(): MemAgentSettings | undefined {
    return this._memAgentSettings;
  }

  // Keep top-level getters for backward compatibility
  get customVocabulary(): string[] {
    return this.voice.customVocabulary;
  }

  get processingInstructions(): string {
    return this.voice.processingInstructions;
  }

  /**
   * Persists a settings patch through the API without waiting for the resulting
   * sync model update to reach the local database. The observable settings
   * snapshot is updated after persistence so immediate consumers see the saved
   * values while inbound sync catches up.
   *
   * @param settingsPatch - The settings patch to apply
   * @returns The persisted settings model ID and version
   * @throws Error if the API call fails or returns no data
   */
  async updateSettingsUsingApi(settingsPatch: SpaceAccountSettingsPatch) {
    logger.info({
      message: "[AppStoreSpaceAccountSettingsStore] updateSettingsUsingApi starting",
      info: { settingsPatch: objectModule.safeAsJson(settingsPatch) },
    });

    const response = await api.patch("/v2/me/settings", {
      body: {
        settings: settingsPatch,
      },
    });

    if (!response.response.ok) {
      logger.error({
        message: "[AppStoreSpaceAccountSettingsStore] API call failed",
        info: { error: objectModule.safeAsJson(response.error) },
      });
      throw new Error("Failed to save settings");
    }

    const responseData = response.data;
    if (!responseData) {
      logger.error({
        message: "[AppStoreSpaceAccountSettingsStore] API response missing data",
      });
      throw new Error("API response missing data");
    }

    logger.info({
      message: "[AppStoreSpaceAccountSettingsStore] API call succeeded",
      info: {
        targetModelId: responseData.model_id,
        targetVersion: responseData.model_version,
      },
    });

    const syncedSettingsSnapshot = this.syncedSettingsSnapshot;
    const hasAlreadySyncedApiVersion =
      syncedSettingsSnapshot !== undefined &&
      syncedSettingsSnapshot.modelId === responseData.model_id &&
      syncedSettingsSnapshot.modelVersion >= responseData.model_version;

    if (!hasAlreadySyncedApiVersion) {
      this.pendingApiSettingsPatches.push({
        modelId: responseData.model_id,
        modelVersion: responseData.model_version,
        settingsPatch,
      });
    }
    this.pendingApiSettingsPatches.sort((a, b) => a.modelVersion - b.modelVersion);
    runInAction(() => this.reconcileSettingsState());

    return responseData;
  }

  /**
   * Updates settings using the sync operation system.
   * This provides optimistic updates and queues the operation for background sync.
   *
   * @param settingsPatch - The settings patch to apply
   */
  async updateSettingsUsingSyncOperation(settingsPatch: SpaceAccountSettingsPatch): Promise<void> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;

    await new UpdateSpaceAccountSettingsOperation({
      store: this.store,
      payload: {
        space_account_id: spaceAccountId,
        settings: settingsPatch,
      },
    }).execute();
  }
}
